import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { requestEmailMagicLink, startXOAuth } from "../../lib/api";

// /auth/login — entry point for both auth paths.
// Two CTAs: Sign in with X (PKCE OAuth) and Sign in with email (magic link).
export function LoginRoute() {
	const [params] = useSearchParams();
	const next = params.get("next") ?? "/";

	const [email, setEmail] = useState("");
	const [emailSubmitting, setEmailSubmitting] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const [xSubmitting, setXSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleX() {
		setError(null);
		setXSubmitting(true);
		try {
			const { authUrl } = await startXOAuth();
			// Persist `next` so the X callback can redirect back to it.
			sessionStorage.setItem("hf_next", next);
			window.location.assign(authUrl);
		} catch (err) {
			setError(err instanceof Error ? err.message : "X sign-in failed");
			setXSubmitting(false);
		}
	}

	async function handleEmail(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setError(null);
		setEmailSubmitting(true);
		try {
			await requestEmailMagicLink(email);
			setEmailSent(true);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Magic link failed");
		} finally {
			setEmailSubmitting(false);
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div className="w-full max-w-md space-y-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-elev)] p-8">
				<header className="space-y-1 text-center">
					<h1 className="text-2xl font-bold">Sign in to Hedge Fam</h1>
					<p className="text-sm text-[var(--color-muted)]">
						Win together, that's a Hedge Fam.
					</p>
				</header>

				{error && (
					<div className="rounded-md border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]">
						{error}
					</div>
				)}

				<button
					onClick={handleX}
					disabled={xSubmitting}
					className="flex w-full items-center justify-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-zinc-200 disabled:opacity-50"
				>
					<span aria-hidden>X</span>
					{xSubmitting ? "Redirecting…" : "Sign in with X"}
				</button>

				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t border-[var(--color-border)]" />
					</div>
					<div className="relative flex justify-center text-xs uppercase tracking-wide">
						<span className="bg-[var(--color-bg-elev)] px-2 text-[var(--color-muted)]">
							or
						</span>
					</div>
				</div>

				{emailSent ? (
					<div className="rounded-md border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/10 p-3 text-sm text-[var(--color-accent)]">
						Check your inbox — magic link sent to {email}.
					</div>
				) : (
					<form onSubmit={handleEmail} className="space-y-3">
						<label className="block">
							<span className="text-sm text-[var(--color-muted)]">
								Email
							</span>
							<input
								type="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
								className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm text-[var(--color-fg)] placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
							/>
						</label>
						<button
							type="submit"
							disabled={emailSubmitting}
							className="w-full rounded-md bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-emerald-400 disabled:opacity-50"
						>
							{emailSubmitting ? "Sending…" : "Sign in with email"}
						</button>
					</form>
				)}

				<p className="text-center text-xs text-[var(--color-muted)]">
					Simulated paper trades. Not investment advice. See{" "}
					<a
						href="https://hedgefam.com/disclosures"
						className="underline hover:text-[var(--color-fg)]"
					>
						disclosures
					</a>
					.
				</p>
			</div>
		</div>
	);
}
